import { Box, Typography } from "@mui/material";
import React from "react";
import {
  useServiceAccount,
  useServiceAccountLoad,
} from "../../../hook/serviceAccount";
import { LoadBoundary } from "../../brand/LoadBoundary";
import { ServiceAccountBlock } from "../../../types/notebook/note/block/serviceAccount";
import {
  serviceAccountName,
  serviceAccountDefaultDevcontainer,
} from "../../../types/serviceAccount";
import { CopyClipboard } from "../../copyClipboard";
import { DynamicTextField } from "../../dynamicTextField";
import { NoteBlockContainer } from "../../note";
import { DevcontainerPicker } from "../../ai-panel/DevcontainerPicker";

export const ServiceAccountBlockComponent: React.FC<{
  block: ServiceAccountBlock;
}> = React.memo(({ block }) => {
  const serviceAccountId = block["service-account-id"];
  const { setServiceAccountName, setDefaultDevcontainer } =
    useServiceAccount(serviceAccountId);
  const { serviceAccount, status, retry } = useServiceAccountLoad(block);
  const name = serviceAccountName(serviceAccount);
  const apiKey = serviceAccount?.["api-key"] ?? "";
  const defaultDc = serviceAccountDefaultDevcontainer(serviceAccount);

  return (
    <NoteBlockContainer name="Service Account">
      <LoadBoundary
        status={status}
        onRetry={retry}
        label="Loading service account"
        errorMessage="Could not load this service account."
        testId="service-account-loading"
      >
      <Box data-testid="service-account-block">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>{serviceAccountId}</Typography>
          <CopyClipboard value={serviceAccountId} />
        </Box>
        <DynamicTextField text={name} setText={setServiceAccountName} />
        <DevcontainerPicker value={defaultDc} onChange={setDefaultDevcontainer} />
        {/* The api-key is copy-only — never rendered as plaintext. */}
        <Box
          data-testid="service-account-api-key-copy"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            API key
          </Typography>
          <CopyClipboard value={apiKey} />
        </Box>
      </Box>
      </LoadBoundary>
    </NoteBlockContainer>
  );
});
